/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/**
 * Enum for the event scheduler type.
 */
export enum EventSchedulerType {
  once = 'once',
  recurring = 'recurring',
}

/**
 * The enum for the event subscription target type accepted by the Event bus.
 */
export enum EventBusSubscriptionTargetType {
  Api = `api`,
  Job = `job`,
}

/**
 * The details property of the API target.
 */
export interface EventBusApiTargetDetails {
  /**
   * The API key to use.
   */
  key: string;

  /**
   * The URL endpoint for the API target.
   */
  url: string;
}

/**
 * Optional custom input transformer.
 * For more information see https://docs.aws.amazon.com/eventbridge/latest/userguide/eb-transform-target-input.html.
 */
export interface EventBusInputTransformer {
  /**
   * The mappings to parameters that can be used in the InputTemplate.
   */
  InputPathsMap: Record<string, string>;

  /**
   * String version of a JSON object that will be used to transform the event before it is sent to the target.
   */
  InputTemplate: string;
}

/**
 * The rule pattern to match events with.
 */
export interface EventBusPattern {
  /**
   * String(s) to identify the action of event being sent.
   */
  action: string;

  /**
   * Event bus filter pattern for event data.
   */
  event?: EventPatternSyntax;

  /**
   * The DXP service(s) sending the event.
   */
  source: string;
}

/**
 * The event pattern required by the Event Bus service, and is the representation of the attribute filters.
 */
export type EventPatternSyntax = Record<string, EventRuleSyntax>;

/**
 * All the possible EventRuleSyntax* types.
 * They represent event bus service syntax of a rule operation.
 */
export type EventRuleSyntax =
  | EventRuleSyntaxEquals
  | EventRuleSyntaxNotEquals
  | EventRuleSyntaxNumeric
  | EventRuleSyntaxBeginsWith
  | EventRuleSyntaxExistence
  | EventRuleSyntaxEmpty
  | EventRuleSyntaxNull;

export type EventRuleSyntaxEquals = [string];
export type EventRuleSyntaxNotEquals = [Record<'anything-but', [string]>];
export type EventRuleSyntaxNumeric = [Record<'numeric', [string, number]>];
export type EventRuleSyntaxBeginsWith = [Record<'prefix', string>];
export type EventRuleSyntaxExistence = [Record<'exists', boolean>];
export type EventRuleSyntaxEmpty = [''];
export type EventRuleSyntaxNull = [null];
